// The chat's languages live in `config/` so Python and the frontend's scripts
// read the same file (`apps/python-api/models/questionnaire/capabilities.py`):
// the python-api image and Argo ship `config/`, never `packages/shared-types/src/`.
import chatLanguagesJson from "../../../../config/questionnaire-chat-languages.json";

/**
 * The language the builder's chat speaks (wave G, D1): the draft's one
 * conversation language. It starts at the builder's interface language when
 * the draft is created and follows the language the turn's first model stage
 * reads the user's message in (`MessageLanguage`, wave H L3); every line the
 * chat shows, written by the model or by code, is in it. The list is the one
 * language list: a survey is worded in the scale-wording packs' languages
 * (`SURVEY_LANGUAGES`), each one of these.
 */
export type ConversationLanguage = keyof typeof chatLanguagesJson.languages;

/**
 * What a turn's first model stage (PLAN, revise or the restructure) returns
 * as `messageLanguage`: the language of the user's latest message, or `same`,
 * its honest none (the chat's own language, a message that shows none, or a
 * command). `conversation-language.ts` records it as the chat's.
 */
export type MessageLanguage = ConversationLanguage | "same";

export const CHAT_LANGUAGES = Object.freeze(
  Object.keys(chatLanguagesJson.languages) as Array<ConversationLanguage>,
);

/** What a chat state stored before the language was kept speaks. */
export const DEFAULT_CONVERSATION_LANGUAGE: ConversationLanguage = "en";

export function isConversationLanguage(
  value: unknown,
): value is ConversationLanguage {
  return (
    typeof value === "string" &&
    (CHAT_LANGUAGES as ReadonlyArray<string>).includes(value)
  );
}

/** A chat state's conversation language, or what a state stored before it was kept speaks. */
export function conversationLanguageOf(state: {
  conversationLanguage?: unknown;
}): ConversationLanguage {
  return isConversationLanguage(state.conversationLanguage)
    ? state.conversationLanguage
    : DEFAULT_CONVERSATION_LANGUAGE;
}

/** Whether this language capitalises its nouns mid-sentence (its registry entry). */
export function capitalisesNouns(language: unknown): boolean {
  return isConversationLanguage(language) && chatLanguagesJson.languages[language].capitalisesNouns;
}

/** The `dir` attribute a line written in this chat language is read with. */
export function chatLanguageDirection(language: unknown): "ltr" | "rtl" {
  return isConversationLanguage(language) &&
    chatLanguagesJson.languages[language].dir === "rtl"
    ? "rtl"
    : "ltr";
}
